import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { JobHttpService } from 'cms/features/feature_job/service';
import EmployeeForm from '../components/EmployeeForm';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    Grid,
    CircularProgress,
    Alert,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';

function EmployeesPage() {
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const queryClient = useQueryClient();

    // Load user's company
    const { data: company, isLoading: companyLoading } = useQuery(
        'myCompany',
        () => JobHttpService.loadMyCompany()
    );

    // Load employees for the company
    const { data: employeesData, isLoading: employeesLoading, isError, error } = useQuery(
        ['employees', company?.id],
        () => {
            if (!company?.id) return { results: [], count: 0 };
            return JobHttpService.loadEmployees({ company: company.id });
        },
        {
            enabled: !!company?.id,
            keepPreviousData: true
        }
    );

    const deleteEmployeeMutation = useMutation(
        (id) => JobHttpService.deleteEmployee(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['employees', company?.id]);
            }
        }
    );

    const employees = employeesData?.results || employeesData || [];

    const handleAddClick = () => {
        setSelectedEmployeeId(null);
        setEditModalOpen(true);
    };

    const handleEditClick = (employeeId) => {
        setSelectedEmployeeId(employeeId);
        setEditModalOpen(true);
    };

    const handleModalClose = () => {
        setEditModalOpen(false);
        setSelectedEmployeeId(null);
    };

    const handleEmployeeSaved = () => {
        queryClient.invalidateQueries(['employees', company?.id]);
    };

    const handleDelete = async (employeeId) => {
        if (window.confirm('Are you sure you want to delete this employee?')) {
            try {
                await deleteEmployeeMutation.mutateAsync(employeeId);
            } catch (err) {
                console.error('Error deleting employee:', err);
                alert('Failed to delete employee. Please try again.');
            }
        }
    };

    if (companyLoading || employeesLoading) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </section>
        );
    }

    if (!company) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        You need to create a company first. <Button href="/employer/my-company">Go to My Company</Button>
                    </Alert>
                </Container>
            </section>
        );
    }

    return (
        <section className="section-box mt-50">
            <Container maxWidth="lg">
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" fontWeight={600} gutterBottom>
                            Employees
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage your company employees
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddClick}
                    >
                        Add Employee
                    </Button>
                </Box>

                {isError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error?.message || 'Failed to load employees'}
                    </Alert>
                )}

                {employees.length === 0 && !employeesLoading && (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            No employees found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Start by adding your first employee
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddClick}
                        >
                            Add Employee
                        </Button>
                    </Paper>
                )}

                {employees.length > 0 && (
                    <Card>
                        <CardContent sx={{ p: 0 }}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Employee Number</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Role</TableCell>
                                            <TableCell>Contact</TableCell>
                                            <TableCell>Location</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {employees.map((employee) => (
                                            <TableRow key={employee.id} hover>
                                                <TableCell>
                                                    {employee.employee_number || '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {employee.first_name} {employee.last_name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {employee.role ? (
                                                        <Chip
                                                            icon={<WorkIcon />}
                                                            label={employee.role}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    ) : (
                                                        '—'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        {employee.email && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <EmailIcon fontSize="small" color="action" />
                                                                <Typography variant="caption">
                                                                    {employee.email}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {employee.phone_number && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <PhoneIcon fontSize="small" color="action" />
                                                                <Typography variant="caption">
                                                                    {employee.phone_number}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {!employee.email && !employee.phone_number && '—'}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {employee.address || employee.city ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <LocationOnIcon fontSize="small" color="action" />
                                                            <Typography variant="caption">
                                                                {[employee.address, employee.city, employee.postal_code]
                                                                    .filter(Boolean)
                                                                    .join(', ')}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditClick(employee.id)}
                                                        color="primary"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDelete(employee.id)}
                                                        color="error"
                                                        disabled={deleteEmployeeMutation.isLoading}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                )}

                {/* Employee Form Modal */}
                <EmployeeForm
                    open={editModalOpen}
                    onClose={handleModalClose}
                    employeeId={selectedEmployeeId}
                    companyId={company.id}
                    onSaved={handleEmployeeSaved}
                />
            </Container>
        </section>
    );
}

export default EmployeesPage;
